import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { KeyValuePair } from '@/types/integration-form-types';

interface KeyValuePairListProps {
    value: KeyValuePair[];
    onChange: (pairs: KeyValuePair[]) => void;
    keyPlaceholder?: string;
    valuePlaceholder?: string;
    addLabel?: string;
    disabled?: boolean;
}

export function KeyValuePairList({
    value,
    onChange,
    keyPlaceholder = 'Key',
    valuePlaceholder = 'Value',
    addLabel = 'Add',
    disabled = false,
}: KeyValuePairListProps) {
    const handleAdd = () => {
        onChange([...value, { key: '', value: '' }]);
    };

    const handleRemove = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    const handleChange = (index: number, field: 'key' | 'value', val: string) => {
        const updated = value.map((pair, i) =>
            i === index ? { ...pair, [field]: val } : pair,
        );
        onChange(updated);
    };

    return (
        <div className="space-y-2">
            {value.map((pair, index) => (
                <div key={index} className="flex items-center gap-2">
                    <Input
                        placeholder={keyPlaceholder}
                        value={pair.key}
                        onChange={(e) => handleChange(index, 'key', e.target.value)}
                        className="flex-1 font-mono text-sm"
                        disabled={disabled}
                    />
                    <Input
                        placeholder={valuePlaceholder}
                        value={pair.value}
                        onChange={(e) => handleChange(index, 'value', e.target.value)}
                        className="flex-1 font-mono text-sm"
                        disabled={disabled}
                    />
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(index)}
                        disabled={disabled}
                        className="h-9 w-9 p-0 text-slate-400 hover:text-red-500"
                    >
                        <Trash2 size={14} />
                    </Button>
                </div>
            ))}
            <Button
                type="button"
                size="sm"
                onClick={handleAdd}
                disabled={disabled}
                className="gap-1 text-xs bg-btn-primary text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <Plus size={12} />
                {addLabel}
            </Button>
        </div>
    );
}
